import { Ban, FileText, Gavel, History, RotateCcw, Settings, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { Menu, type MenuItem } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { Skeleton } from '../../../../components/Skeleton';
import { StatusText } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { CaseFilesPanel } from '../components/CaseFilesPanel';
import { PostOperationReportModal } from '../components/PostOperationReportModal';
import { FLAG, hasFlag, POR_AUDIT_ENTITY_TYPES, REPORT_FILE_MAX_BYTES, type PostOperationReportListItem } from '../caseTypes';
import { postOperationReportsApi } from '../postOperationReportsApi';

type TabKey = 'info' | 'attachments';

/**
 * One Post Operation Report — its summary, and the files filed with it.
 *
 * <b>It has no access of its own.</b> Its CASE decides: a reader who is not a member is told the
 * report does not exist, one below the case's level is refused, and every control is disabled by
 * the case's flag for the act. The case is one link away, in the breadcrumb and the menu.
 */
export function PostOperationReportProfilePage() {
  const { caseId, reportId } = useParams<{ caseId: string; reportId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [record, setRecord] = useState<PostOperationReportListItem | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [tab, setTab] = useState<TabKey>('info');
  const [modal, setModal] = useState<'edit' | 'history' | 'delete' | 'restore' | null>(null);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  // STABLE — the files panel's fetch depends on it.
  const files = useMemo(() => postOperationReportsApi.files(reportId ?? ''), [reportId]);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!reportId) return;

      postOperationReportsApi
        .get(reportId, signal)
        .then((next) => {
          if (signal?.aborted) return;
          setRecord(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this report.', 0));
        });
    },
    [reportId],
  );

  useEffect(() => {
    // A different report drops what is on screen first.
    setRecord(null);
    setError(null);

    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const breadcrumb: BreadcrumbSegment[] = [
    'Case Management',
    { label: 'Cases', to: '/cases' },
    { label: record?.caseNumber ?? 'Case', to: `/cases/${caseId}` },
    record?.title ?? 'Post Operation Report',
  ];

  if (error && !record) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        {error.isForbidden ? (
          <EmptyState message="This report's case is above your access level. Its reports open only for members cleared for that level." />
        ) : (
          <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
        )}
      </div>
    );
  }

  if (!record) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const flags = record.permissions;
  const caseReason = record.caseIsActive ? null : 'This case is deleted.';
  const writeReason =
    caseReason ??
    (!record.isActive
      ? 'This report is deleted. Restore it first.'
      : hasFlag(flags, FLAG.upload)
        ? null
        : 'You have not been given Upload on this case.');
  const editReason =
    caseReason ??
    (!record.isActive
      ? 'This report is deleted. Restore it first.'
      : hasFlag(flags, FLAG.edit)
        ? null
        : 'You have not been given Edit on this case.');
  const deleteReason = caseReason ?? (hasFlag(flags, FLAG.delete) ? null : 'You have not been given Delete on this case.');

  async function setActive(active: boolean) {
    if (!record) return;

    setActing(true);
    setActionError(null);

    try {
      if (active) {
        await postOperationReportsApi.activate(record.id);
        toast.success(`${record.title} was restored.`);
      } else {
        await postOperationReportsApi.deactivate(record.id);
        toast.success(`${record.title} was deleted.`);
      }

      setModal(null);
      load();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setActing(false);
    }
  }

  const actions: MenuItem[] = [
    {
      key: 'case',
      label: 'Open case record',
      icon: <Gavel className="h-4 w-4" aria-hidden="true" />,
      permission: 'cases.read',
      onClick: () => navigate(`/cases/${record.caseId}`),
    },
    {
      key: 'history',
      label: 'History Logs',
      icon: <History className="h-4 w-4" aria-hidden="true" />,
      permission: 'audit-logs.read',
      onClick: () => setModal('history'),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <Trash2 className="h-4 w-4" aria-hidden="true" />,
      permission: 'post-operation-reports.delete',
      destructive: true,
      disabledReason: !record.isActive ? 'This report is already deleted.' : deleteReason,
      onClick: () => setModal('delete'),
    },
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell<TabKey>
        id="post-operation-report-profile"
        panel={tab === 'info' ? 'cards' : 'bleed'}
        header={
          <ProfileHeader
            id="post-operation-report-profile-header"
            icon={<FileText className="h-6 w-6" aria-hidden="true" />}
            title={record.title}
            meta={[record.caseNumber]}
            status={
              <StatusText tone={record.isActive ? 'active' : 'inactive'}>{record.isActive ? 'Active' : 'Deleted'}</StatusText>
            }
            createdBy={record.createdByName}
            createdAt={record.createdAt}
            actions={
              <Menu
                id="post-operation-report-profile-settings"
                items={actions}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    id="post-operation-report-profile-settings-trigger"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Report settings"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          actionError ? (
            <ProfileBanner
              id="post-operation-report-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={actionError.message}
            />
          ) : !record.isActive ? (
            <ProfileBanner
              id="post-operation-report-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This report is deleted. It no longer appears on its case; its summary and files are kept."
              action={
                deleteReason === null ? (
                  <PermissionGate permission="post-operation-reports.delete">
                    <Button
                      id="post-operation-report-profile-restore"
                      variant="secondary"
                      leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                      onClick={() => setModal('restore')}
                      disabled={acting}
                      loading={acting}
                      loadingLabel="Restoring"
                    >
                      Restore
                    </Button>
                  </PermissionGate>
                ) : undefined
              }
            />
          ) : (
            <ProfileBanner
              id="post-operation-report-profile-banner"
              icon={<FileText className="h-4 w-4" aria-hidden="true" />}
              message={`Filed on case ${record.caseNumber}. Whoever may open the case may read this report; its files are kept on the Attachments tab.`}
            />
          )
        }
        tabs={[
          { key: 'info', label: 'Info' },
          { key: 'attachments', label: <>Attachments <CountBadge count={record.attachmentCount} /></> },
        ]}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === 'info' ? (
          <>
            <ProfileCard
              id="post-operation-report-profile-details"
              title="Report"
              menuItems={[
                {
                  key: 'edit',
                  label: 'Edit',
                  permission: 'post-operation-reports.update',
                  disabledReason: editReason,
                  onClick: () => setModal('edit'),
                },
              ]}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Title', value: record.title },
                  { label: 'Case', value: record.caseNumber, mono: true },
                  { label: 'Summary', value: <span className="whitespace-pre-wrap">{record.summary}</span> },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="post-operation-report-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: record.createdByName },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(record.createdAt)} className="font-mono">
                        {formatDateTime(record.createdAt)}
                      </time>
                    ),
                  },
                  { label: 'Updated by', value: record.updatedByName },
                  {
                    label: 'Updated',
                    value: record.updatedAt ? (
                      <time dateTime={toIso(record.updatedAt)} className="font-mono">
                        {formatDateTime(record.updatedAt)}
                      </time>
                    ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : null}

        {tab === 'attachments' ? (
          <div className="flex h-full min-h-0 min-w-0 flex-col p-4">
            <CaseFilesPanel
              id="post-operation-report-files"
              files={files}
              maxBytes={REPORT_FILE_MAX_BYTES}
              recordLabel="this report"
              attachPermission="post-operation-reports.attach"
              downloadPermission="post-operation-reports.download"
              writeReason={writeReason}
              onChanged={() => load()}
            />
          </div>
        ) : null}
      </ProfileHeaderShell>

      {modal === 'edit' ? (
        <PostOperationReportModal
          caseId={record.caseId}
          editing={record}
          onClose={() => setModal(null)}
          onSaved={(saved) => {
            setModal(null);
            toast.success(`${saved.title} was saved.`);
            load();
          }}
        />
      ) : null}

      {modal === 'history' ? (
        <HistoryLogsModal
          open
          onClose={() => setModal(null)}
          module="post-operation-reports"
          entityTypes={POR_AUDIT_ENTITY_TYPES}
          entityId={record.id}
        />
      ) : null}

      {modal === 'delete' || modal === 'restore' ? (
        <ConfirmDialog
          open
          onClose={() => {
            setModal(null);
            setActionError(null);
          }}
          onConfirm={() => void setActive(modal === 'restore')}
          title={modal === 'delete' ? 'Delete report' : 'Restore report'}
          danger={modal === 'delete'}
          requireConfirm
          loading={acting}
          id={`post-operation-report-profile-${modal}-dialog`}
          body={
            <p>
              {modal === 'delete'
                ? `${record.title} stops appearing on case ${record.caseNumber}. Nothing is erased — its files are kept, and it can be restored.`
                : `${record.title} appears on case ${record.caseNumber} again, with its files.`}
            </p>
          }
        />
      ) : null}
    </div>
  );
}
